import { queryKeyFn } from '@sit/client-shared';
import { getCompanyFromWebSessionStorage } from '@web/helpers/session';
import { queryClient } from '@web/hooks/api/query/client';
import isEqual from 'lodash/isEqual';
import { all, call, put } from 'redux-saga/effects';
import api from '../../helpers/api';
import { fetchQueryTimesheetRows } from '../../hooks/api';
import * as ClustersActions from '../Clusters/actions';
import { updateSyncStatus } from '../Timesheet/actions';
import * as TimesheetEntriesActions from '../TimesheetEntries/actions';
import { selectToken, selectUserData } from '../selectors';
import { store } from '../store';
import * as actions from './actions';
import { GenerateRowAutofill, HandleAutofillValues } from './helpers';

export function* createTimesheetRowRequestSaga(payload) {
  try {
    const _store = store.getState();
    const { id: companyId } = getCompanyFromWebSessionStorage() ?? { id: undefined };
    const userData = selectUserData(_store);
    const token = selectToken(_store);
    const res = yield api.send({
      url: `/timesheets/${payload.data.timesheetId}/rows${payload.data.staff ? '?staff=true' : ''}`,
      method: 'POST',
      data: payload.data.data,
      token,
    });
    let userAutofillValues = [];
    if (userData?.id === payload.data.timesheetUserId) {
      userAutofillValues = userData?.autofillValues;
    } else {
      // this is a staff timesheet, need to get the autofill values from the timesheet user
      // yield put(EmployeesActions.getEmployeesRequest({}));
      /** @type {[import('@tanstack/react-query').QueryKey, import('@sit/client-shared').GetApiUsersResponse | undefined][]} */
      const employeesData = queryClient.getQueriesData(
        queryKeyFn({
          path: '/users/list',
          operationId: 'getApiUsers',
          variables: {
            companyId,
          },
        }),
      );
      let timesheetUser;
      const userIdOfTimesheet = payload.data.userId || payload.data.timesheetUserId;
      for (const [queryKey, queryData] of employeesData) {
        // ['users', 'list', { companyId }]
        if (queryKey.length === 3) {
          timesheetUser = queryData?.users.find((u) => u.id === userIdOfTimesheet);
          if (timesheetUser) break;
        }
      }
      userAutofillValues = timesheetUser?.autofillValues;
    }
    if (userAutofillValues && userAutofillValues.length > 0) {
      yield HandleAutofillValues({ autofillValues: userAutofillValues, rowId: res.id, from: 'user' });
    }
    yield put(actions.createTimesheetRowRequestSuccess(res));
    yield put(actions.getTimesheetRowRequest({ timesheetId: payload.data.timesheetId }));
    yield put(updateSyncStatus(false));
  } catch (error) {
    yield put(actions.createTimesheetRowRequestFailure(error));
  }
}

export function* deleteTimesheetRowRequestSaga(payload) {
  try {
    yield api.send({
      url: `/timesheets/${payload.data.timesheetId}/rows/${payload.data.rowId}`,
      method: 'DELETE',
      token: selectToken(store.getState()),
    });
    yield put(actions.deleteTimesheetRowRequestSuccess({ rowId: payload.data.rowId }));
    yield put(TimesheetEntriesActions.getTimesheetEntriesRequest({ timesheetId: payload.data.timesheetId }));
    yield put(actions.getTimesheetRowRequest({ timesheetId: payload.data.timesheetId }));
    yield put(ClustersActions.getClustersRequest({ id: payload.data.timesheetId }));
    yield put(updateSyncStatus(false));
  } catch (error) {
    yield put(actions.deleteTimesheetRowRequestFailure(error));
  }
}

export function* getTimesheetRowRequestSaga(payload) {
  try {
    const _store = store.getState();
    const res = yield api.send({
      url: `/timesheets/${payload.data.timesheetId}/rows/${payload.data.rowId}`,
      method: 'GET',
      token: selectToken(_store),
    });
    yield call(GenerateRowAutofill, { res, store: _store });
    yield put(actions.getTimesheetRowRequestSuccess(res));
  } catch (error) {
    yield put(actions.getTimesheetRowRequestFailure(error));
  }
}

export function* getTimesheetRowsRequestSaga(payload) {
  try {
    const _store = store.getState();
    const existingTimesheetRows = _store.timesheetRows.data || [];
    const timesheetRows = yield fetchQueryTimesheetRows({ timesheetId: payload.data.timesheetId }, selectToken(_store));
    // Compare existingTimesheetRows to timesheetRows
    const rowsToUpdateAutofills = [];
    for (const newRow of timesheetRows) {
      const existingRow = existingTimesheetRows.find((row) => row.id === newRow.id);

      if (!existingRow || !isEqual(newRow, existingRow)) {
        // If the row is new or updated, perform GenerateRowAutofill
        rowsToUpdateAutofills.push(newRow);
      }
    }

    yield all(rowsToUpdateAutofills.map((row) => call(GenerateRowAutofill, { row, store: _store })));
    yield put(actions.getTimesheetRowsRequestSuccess(timesheetRows));
  } catch (error) {
    yield put(actions.getTimesheetRowsRequestFailure(error));
  }
}

export function* toggleRowBillableRequestSaga(payload) {
  try {
    const res = yield api.send({
      url: `/timesheets/${payload.data.timesheetId}/rows/${payload.data.rowId}/billable`,
      method: 'PUT',
      data: payload.data.data,
      token: selectToken(store.getState()),
    });
    yield put(updateSyncStatus(false));
    yield put(actions.toggleRowBillableRequestSuccess(res));
    yield put(actions.getTimesheetRowRequest({ timesheetId: payload.data.timesheetId }));
  } catch (error) {
    yield put(actions.toggleRowBillableRequestFailure(error));
  }
}

export function* updateTimesheetRowRequestSaga(payload) {
  try {
    const res = yield api.send({
      url: `/timesheets/${payload.data.timesheetId}/rows/${payload.data.rowId}`,
      method: 'PUT',
      data: payload.data.data,
      token: selectToken(store.getState()),
    });
    yield put(actions.updateTimesheetRowRequestSuccess(res));
    yield put(actions.getTimesheetRowRequest({ timesheetId: payload.data.timesheetId }));
    yield put(updateSyncStatus(false));
  } catch (error) {
    yield put(actions.updateTimesheetRowRequestFailure(error));
  }
}
